/**
 * API types for AI Engine Config CRUD (`accounts/ai-engine-config/`).
 */

/** Frequency values accepted by the backend. */
export type TAIEngineConfigFrequencyApi = "daily" | "weekly" | "monthly";

/** Day-of-week values accepted by the backend. */
export type TAIEngineConfigDayOfWeekApi =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

/** Nested employee object returned on agent/auditor relations. */
export type TAIEngineConfigEmployee = {
  internal_id: string;
  first_name: string;
  last_name: string;
};

/** Nested campaign object returned on campaign relations. */
export type TAIEngineConfigCampaign = {
  internal_id: string;
  name: string;
};

/** Nested AI engine object (or UUID string) returned on `ai_engine`. */
export type TAIEngineConfigEngine = {
  internal_id: string;
  name?: string | null;
  slug?: string | null;
};

/** Score-range allocation stored as `score_configurations`. */
export type TAIEngineConfigScore = {
  min_score: number;
  max_score: number;
  sample: number;
};

/** List/detail record returned by the AI Engine Config API. */
export type TAIEngineConfig = {
  internal_id: string;
  agent: TAIEngineConfigEmployee[];
  auditor: TAIEngineConfigEmployee[];
  campaign: TAIEngineConfigCampaign[];
  ai_engine: TAIEngineConfigEngine | string | null;
  ai_compliance: boolean | null;
  frequency: TAIEngineConfigFrequencyApi;
  day_of_week?: TAIEngineConfigDayOfWeekApi | null;
  day_of_month?: number | null;
  sample: number;
  scheduler_time?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  score_configurations?: TAIEngineConfigScore[];
  all_selected_campaigns?:boolean;
  all_selected_agents?:boolean;
};

/** Create/update payload. Scope fields are mutually exclusive on the backend. */
export type TAIEngineConfigPayload = {
  agent?: string[];
  auditor?: string[];
  campaign?: string[];
  ai_engine: string;
  ai_compliance: boolean | null;
  frequency: TAIEngineConfigFrequencyApi;
  day_of_week?: TAIEngineConfigDayOfWeekApi;
  day_of_month?: number;
  sample: number;
  scheduler_time?: string;
  start_date?: string;
  end_date?: string;
  score_configurations?: TAIEngineConfigScore[];
  all_selected_campaigns?:boolean;
  all_selected_agents?:boolean;
};

/** Query params for the paginated configurations list. */
export type TAIEngineConfigListParams = {
  page?: number;
  page_size?: number;
  search?: string;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  frequency?: string;
  ai_engine?: string;
  ai_compliance?: string;
  start_date?: string;
  end_date?: string;
};

/** Paginated list response from `accounts/ai-engine-config/`. */
export type TAIEngineConfigListResponse = {
  status: string;
  message?: string;
  data: {
    count: number;
    next: string | null;
    previous: string | null;
    total_pages?: number;
    current_page?: number;
    results: TAIEngineConfig[];
  };
};

/** Create/update/detail response wrapping a single configuration. */
export type TAIEngineConfigResponse = {
  status: string;
  message?: string;
  data: TAIEngineConfig;
};

/** Paginated page of AI engine options from `ai_engine_list/`. */
export type TAIEngineOptionsPage = {
  count?: number;
  current_page?: number;
  total_pages?: number;
  next?: string | null;
  previous?: string | null;
  results: TAIEngineConfigEngine[];
};

/** Wrapper for the AI engine options list. */
export type TAIEngineOptionsResponse = {
  status: boolean | string;
  message?: string;
  data: TAIEngineOptionsPage;
};

/** Frequency labels used by the form select. */
export const AI_ENGINE_CONFIG_FREQUENCIES = [
  "Weekly",
  "Daily",
  "Monthly",
] as const;

export type TAIEngineConfigFrequency =
  (typeof AI_ENGINE_CONFIG_FREQUENCIES)[number];

/** Yes/No/All labels used by the AI Compliance select. All maps to `null` in the API. */
export type TAiCompliance = "Yes" | "No" | "All";

/** Day-of-week labels used by the Weekly scheduler select. */
export const AI_ENGINE_CONFIG_DAYS_OF_WEEK = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
] as const;

export type TDayOfWeek = (typeof AI_ENGINE_CONFIG_DAYS_OF_WEEK)[number];

/** Day-of-month options (1–31) used by the Monthly scheduler select. */
export const AI_ENGINE_CONFIG_DAYS_OF_MONTH = Array.from(
  { length: 31 },
  (_, i) => String(i + 1),
);

/** Score-range row in the list/edit UI model. */
export type TScoreBand = {
  from: number;
  to: number;
  percent: number;
};

/** Optional scheduler display parts on a configuration row. */
export type TAIEngineConfigSchedule = {
  time?: string;
  dayOfWeek?: TDayOfWeek;
  dayOfMonth?: string;
};

/** List/edit UI model mapped from an API configuration. */
export type TAIEngineConfiguration = {
  id: string;
  agentIds: string[];
  agentNames: string[];
  auditorIds: string[];
  auditorNames: string[];
  campaignIds: string[];
  campaignNames: string[];
  aiCompliance: TAiCompliance;
  samplePercent: number;
  frequency: TAIEngineConfigFrequency;
  aiEngineId: string;
  aiEngineName: string;
  schedule?: TAIEngineConfigSchedule;
  bands?: TScoreBand[];
  startDate: string;
  endDate: string;
  allSelectedAgents:boolean;
  allSelectedCampaigns:boolean;
};

/** Score-range inputs stored as strings in React Hook Form. */
export type TScoreBandForm = {
  from: string;
  to: string;
  percent: string;
};

/** Scope grouping used by the form (UI-only; not sent to the API). */
export const AI_ENGINE_CONFIG_GROUP_BY_OPTIONS = [
  "agent",
  "auditor",
  "campaign",
  "date_range",
] as const;

export type TAIEngineConfigGroupBy =
  (typeof AI_ENGINE_CONFIG_GROUP_BY_OPTIONS)[number];

/** Validated add/edit form values. `bands` is null when bifurcation is unused. */
export type TAIEngineConfigurationFormState = {
  aiCompliance: TAiCompliance | "";
  samplePercent: string;
  frequency: TAIEngineConfigFrequency | "";
  aiEngineId: string;
  /** Which scope filter is active; mirrors Call Sampling "Group calls by". */
  groupBy: TAIEngineConfigGroupBy;
  agentIds: string[];
  auditorIds: string[];
  campaignIds: string[];
  dayOfWeek: TDayOfWeek | "";
  dayOfMonth: string;
  time: string;
  bands: TScoreBandForm[] | null;
  startDate: string;
  endDate: string;
  all_selected_campaigns:boolean;
  all_selected_agents:boolean;
};
